import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  Stack,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
  Tooltip,
  Slider,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import {
  Settings,
  Close as CloseIcon,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Palette,
  AutoFixHigh,
  TextFields
} from '@mui/icons-material';
import { HexColorPicker } from 'react-colorful';
import { fabric } from 'fabric';
import FontsPanel from './panels/FontPreview';

// Font families - verified Google Fonts
const fontFamilies = [
  // Modern Sans Serif
  'Poppins', 'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Nunito Sans', 'Work Sans',
  'Quicksand', 'Source Sans Pro', 'Raleway', 'Ubuntu', 'Mulish', 'DM Sans', 'Rubik', 'Karla',
  'Outfit', 'Plus Jakarta Sans', 'Manrope', 'Lexend', 'Public Sans', 'Barlow',
  // New Modern Sans Serif
  'Sora', 'Space Grotesk', 'Archivo', 'Urbanist', 'Jost', 'Figtree', 'Epilogue', 
  'Kumbh Sans', 'Instrument Sans', 'Noto Sans', 'Heebo',
  // Classic Sans Serif
  'Arial', 'Helvetica', 'Verdana', 'Trebuchet MS', 'Tahoma', 'Calibri', 'Geneva', 'Segoe UI',
  // Serif
  'Times New Roman', 'Playfair Display', 'Merriweather', 'Lora', 'Fraunces', 
  'Libre Baskerville', 'Source Serif Pro', 'Crimson Pro', 'Cormorant Garamond',
  'Newsreader', 'EB Garamond', 'Spectral', 'Bitter', 'Arvo',
  // Display/Headline
  'Dancing Script', 'Bebas Neue', 'Abril Fatface', 'Oswald', 'Pacifico', 'Righteous', 
  'Permanent Marker', 'Caveat', 'Comfortaa', 'Lobster', 'Anton', 'Teko', 'Staatliches',
  // Monospace
  'Roboto Mono', 'Source Code Pro', 'JetBrains Mono', 'Space Mono', 'Fira Mono', 
  'IBM Plex Mono', 'Inconsolata', 'Courier Prime', 'DM Mono', 'Courier New'
];

// Preset colors
const presetColors = [
  '#000000', '#FFFFFF', '#3B82F6', '#EF4444', '#10B981', '#F59E0B',
  '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
  '#1F2937', '#6B7280', '#9CA3AF', '#D1D5DB', '#FEE2E2', '#DBEAFE'
];

const ImageRightProperties = ({
  selectedObject,
  fabricCanvasRef,
  setSelectedObject,
  handleImageUpload,
  updateHistory,
  removeBackground,
  removeBackgroundAI,
  isRemovingBackground,
  setSnackbar,
  applyStyleToSelectedText,
  hasTextSelection
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [hexValue, setHexValue] = useState('');
  const [showFontLibrary, setShowFontLibrary] = useState(false);

  // Update hex value when color picker is shown or selected object changes
  useEffect(() => {
    if (showColorPicker && selectedObject && selectedObject.fill) {
      setHexValue(selectedObject.fill);
    }
  }, [showColorPicker, selectedObject]);

  if (!selectedObject) return null;

  // Helper function to update object property
  const updateObjectProperty = (property, value) => {
    if (selectedObject && fabricCanvasRef.current) {
      // Special handling for text styling properties when text is selected
      if (['fill', 'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'textDecoration'].includes(property) && hasTextSelection) {
        // Try to apply style to selected text
        const wasApplied = applyStyleToSelectedText(property, value);
        if (wasApplied) {
          return; // Style was applied to selection, no need to update whole object
        }
      }
      
      // Regular property update for whole object
      selectedObject.set(property, value);
      
      // Special handling for text objects
      if (selectedObject.type === 'textbox') {
        selectedObject.initDimensions();
        selectedObject.setCoords();
      }
      
      fabricCanvasRef.current.renderAll();
      setSelectedObject(fabricCanvasRef.current.getActiveObject());
      updateHistory();
    }
  };

  // Handle font selection from the font library
  const handleFontSelect = (font) => {
    updateObjectProperty('fontFamily', font);
    setShowFontLibrary(false);
  };

  // Custom panel for replaceable images (groups)
  if (selectedObject.isPlaceholderReplacement) {
    const imageInGroup = selectedObject.getObjects('image')[0];
    if (!imageInGroup) return null;

    const handleImagePropChange = (prop, value) => {
      imageInGroup.set(prop, value).setCoords();
      selectedObject.setCoords();
      fabricCanvasRef.current.requestRenderAll();
      updateHistory();
    };

    const handleImageScaleChange = (value) => {
      imageInGroup.set({ scaleX: value, scaleY: value }).setCoords();
      selectedObject.setCoords();
      fabricCanvasRef.current.requestRenderAll();
      updateHistory();
    };
    
    return (
      <Paper sx={{ 
        position: 'absolute',
        right: 0,
        top: 0,
        width: 320, 
        height: '100%', 
        borderRadius: 0, 
        borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
        overflow: 'auto',
        p: 0,
        bgcolor: '#fafbfc',
        zIndex: 999,
        boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.15)'
      }}>
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          bgcolor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" sx={{ 
            mb: 0, 
            fontWeight: 'bold',
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Settings sx={{ fontSize: 20, color: '#4890fe' }} />
            Properties
          </Typography>
          <Tooltip title="Close Properties" arrow>
            <IconButton 
              size="small" 
              onClick={() => {
                fabricCanvasRef.current.discardActiveObject();
                fabricCanvasRef.current.renderAll();
                setSelectedObject(null);
              }}
              sx={{
                color: '#64748b',
                '&:hover': { 
                  bgcolor: 'rgba(100, 116, 139, 0.08)',
                  color: '#374151'
                }
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Box sx={{ p: 2 }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Replace Image
              </Typography>
              <Button
                variant="contained"
                component="label"
                fullWidth
              >
                Upload New Image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleImageUpload(file, selectedObject);
                    }
                    e.target.value = null;
                  }}
                />
              </Button>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Adjust Position
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Offset X"
                    type="number"
                    value={Math.round(imageInGroup.left)}
                    onChange={e => handleImagePropChange('left', parseInt(e.target.value, 10))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Offset Y"
                    type="number"
                    value={Math.round(imageInGroup.top)}
                    onChange={e => handleImagePropChange('top', parseInt(e.target.value, 10))}
                  />
                </Grid>
              </Grid>
            </Box>
            
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Zoom
              </Typography>
              <Slider 
                value={imageInGroup.scaleX}
                min={0.1}
                max={5}
                step={0.05}
                valueLabelDisplay="auto"
                valueLabelFormat={v => `${(v * 100).toFixed(0)}%`}
                onChange={(e, val) => handleImageScaleChange(val)}
              />
            </Box>

            {/* AI Background Removal Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Background Removal
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      if (imageInGroup) {
                        removeBackgroundAI(imageInGroup);
                      } else {
                        setSnackbar({ 
                          open: true, 
                          message: 'No image selected for AI background removal', 
                          severity: 'warning' 
                        });
                      }
                    }}
                    disabled={isRemovingBackground}
                    startIcon={isRemovingBackground ? <CircularProgress size={16} /> : <AutoFixHigh />}
                    sx={{ 
                      bgcolor: '#8B5CF6',
                      '&:hover': { 
                        bgcolor: '#7C3AED'
                      },
                      '&:disabled': {
                        bgcolor: '#D1D5DB',
                        color: '#9CA3AF'
                      }
                    }}
                  >
                    Remove Background (AI)
                  </Button>
                </Grid>
              </Grid>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                AI-powered removal works best with people and common objects.
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Paper>
    );
  }

  const commonProperties = (
    <>
      {/* Position Controls */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
          Position
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="X"
              type="number"
              value={Math.round(selectedObject.left)}
              onChange={(e) => updateObjectProperty('left', parseInt(e.target.value))}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Y"
              type="number"
              value={Math.round(selectedObject.top)}
              onChange={(e) => updateObjectProperty('top', parseInt(e.target.value))}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Opacity Control */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
          Opacity
        </Typography>
        <Slider
          value={selectedObject.opacity * 100}
          onChange={(e, newValue) => updateObjectProperty('opacity', newValue / 100)}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => `${value}%`}
        />
      </Box>
    </>
  );

  // Text-specific properties
  if (selectedObject.type === 'text' || selectedObject.type === 'textbox' || selectedObject.type === 'i-text') {
    return (
      <Paper sx={{ 
        position: 'absolute',
        right: 0,
        top: 0,
        width: 320, 
        height: '100%', 
        borderRadius: 0, 
        borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
        overflow: 'auto',
        p: 0,
        bgcolor: '#fafbfc',
        zIndex: 999,
        boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.15)'
      }}>
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          bgcolor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" sx={{ 
            mb: 0, 
            fontWeight: 'bold',
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Settings sx={{ fontSize: 20, color: '#4890fe' }} />
            Text Properties
          </Typography>
          <Tooltip title="Close Properties" arrow>
            <IconButton 
              size="small" 
              onClick={() => {
                fabricCanvasRef.current.discardActiveObject();
                fabricCanvasRef.current.renderAll();
                setSelectedObject(null);
              }}
              sx={{
                color: '#64748b',
                '&:hover': { 
                  bgcolor: 'rgba(100, 116, 139, 0.08)',
                  color: '#374151'
                }
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Box sx={{ p: 2 }}>
          <Stack spacing={3}>
            {commonProperties}
            
            {/* Text Color - Moved to top as most essential */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
                Text Color
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {presetColors.map((color) => (
                  <Box
                    key={color}
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: color,
                      borderRadius: 0.5,
                      cursor: 'pointer',
                      border: selectedObject.fill === color ? '2px solid #000' : '1px solid #e0e0e0',
                    }}
                    onClick={() => updateObjectProperty('fill', color)}
                  />
                ))}
                <IconButton
                  size="small"
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  sx={{ border: '1px solid #e0e0e0' }}
                >
                  <Palette fontSize="small" />
                </IconButton>
              </Box>
              {showColorPicker && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
                  <HexColorPicker
                    color={selectedObject.fill}
                    onChange={(color) => {
                      updateObjectProperty('fill', color);
                      setHexValue(color);
                    }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Hex Color"
                    value={hexValue}
                    onChange={(e) => setHexValue(e.target.value)}
                    onBlur={() => {
                      // Apply color when field loses focus
                      if (/^#([0-9A-F]{3}){1,2}$/i.test(hexValue)) {
                        updateObjectProperty('fill', hexValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      // Apply color on Enter key
                      if (e.key === 'Enter' && /^#([0-9A-F]{3}){1,2}$/i.test(hexValue)) {
                        updateObjectProperty('fill', hexValue);
                        e.preventDefault();
                      }
                    }}
                    sx={{ mt: 2 }}
                    InputProps={{
                      startAdornment: (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '4px',
                            backgroundColor: hexValue,
                            marginRight: 1,
                            border: '1px solid #e0e0e0'
                          }}
                        />
                      )
                    }}
                  />
                </Box>
              )}
            </Box>
            
            {/* Text Content */}
            <TextField
              fullWidth
              multiline
              label="Text Content"
              value={selectedObject.text}
              onChange={(e) => updateObjectProperty('text', e.target.value)}
            />

            {/* Font Properties */}
            <FormControl fullWidth>
              <InputLabel>Font Family</InputLabel>
              <Select
                value={selectedObject.fontFamily}
                onChange={(e) => updateObjectProperty('fontFamily', e.target.value)}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
                endAdornment={
                  <IconButton 
                    size="small" 
                    sx={{ mr: 2 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowFontLibrary(true);
                    }}
                  >
                    <TextFields fontSize="small" />
                  </IconButton>
                }
              >
                <MenuItem disabled value="">
                  <em>Modern Sans Serif</em>
                </MenuItem>
                {fontFamilies.slice(0, 22).map((font) => (
                  <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                    {font}
                  </MenuItem>
                ))}
                
                <MenuItem disabled value="">
                  <em>New Modern Sans Serif</em>
                </MenuItem>
                {fontFamilies.slice(22, 33).map((font) => (
                  <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                    {font}
                  </MenuItem>
                ))}
                
                <MenuItem disabled value="">
                  <em>Classic Sans Serif</em>
                </MenuItem>
                {fontFamilies.slice(33, 41).map((font) => (
                  <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                    {font}
                  </MenuItem>
                ))}
                
                <MenuItem disabled value="">
                  <em>Serif</em>
                </MenuItem>
                {fontFamilies.slice(41, 54).map((font) => (
                  <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                    {font}
                  </MenuItem>
                ))}
                
                <MenuItem disabled value="">
                  <em>Display/Headline</em>
                </MenuItem>
                {fontFamilies.slice(54, 66).map((font) => (
                  <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                    {font}
                  </MenuItem>
                ))}
                
                <MenuItem disabled value="">
                  <em>Monospace</em>
                </MenuItem>
                {fontFamilies.slice(66).map((font) => (
                  <MenuItem key={font} value={font} sx={{ fontFamily: font }}>
                    {font}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Font Size and Weight in one row */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
            {/* Font Size */}
            <TextField
              fullWidth
              type="number"
              label="Font Size"
              value={selectedObject.fontSize}
              onChange={(e) => updateObjectProperty('fontSize', parseInt(e.target.value))}
            />
              </Grid>
              <Grid item xs={6}>
            {/* Font Weight */}
            <FormControl fullWidth>
                  <InputLabel>Weight</InputLabel>
              <Select
                    value={selectedObject.fontWeight || 'normal'}
                onChange={(e) => updateObjectProperty('fontWeight', e.target.value)}
              >
                <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="400">Regular</MenuItem>
                    <MenuItem value="500">Medium</MenuItem>
                    <MenuItem value="600">Semi Bold</MenuItem>
                    <MenuItem value="700">Bold</MenuItem>
              </Select>
            </FormControl>
              </Grid>
            </Grid>

            {/* Text Alignment */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Alignment
              </Typography>
              <ButtonGroup fullWidth>
                <Button
                  variant={selectedObject.textAlign === 'left' ? 'contained' : 'outlined'}
                  onClick={() => updateObjectProperty('textAlign', 'left')}
                >
                  <FormatAlignLeft />
                </Button>
                <Button
                  variant={selectedObject.textAlign === 'center' ? 'contained' : 'outlined'}
                  onClick={() => updateObjectProperty('textAlign', 'center')}
                >
                  <FormatAlignCenter />
                </Button>
                <Button
                  variant={selectedObject.textAlign === 'right' ? 'contained' : 'outlined'}
                  onClick={() => updateObjectProperty('textAlign', 'right')}
                >
                  <FormatAlignRight />
                </Button>
              </ButtonGroup>
            </Box>

            {/* Line Height (for paragraphs) */}
            {selectedObject.type === 'textbox' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Line Height
                </Typography>
                <Slider
                  value={selectedObject.lineHeight || 1}
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(e, newValue) => updateObjectProperty('lineHeight', newValue)}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => value.toFixed(1)}
                />
              </Box>
            )}

            {/* Text Width (for paragraphs) */}
            {selectedObject.type === 'textbox' && (
              <TextField
                fullWidth
                type="number"
                label="Text Width"
                value={selectedObject.width}
                onChange={(e) => updateObjectProperty('width', parseInt(e.target.value))}
              />
            )}
          </Stack>
        </Box>
      </Paper>
    );
  }

  // Shape-specific properties
  if (selectedObject.type === 'rect' || selectedObject.type === 'circle' || selectedObject.type === 'line') {
    return (
      <Paper sx={{ 
        position: 'absolute',
        right: 0,
        top: 0,
        width: 320, 
        height: '100%', 
        borderRadius: 0, 
        borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
        overflow: 'auto',
        p: 0,
        bgcolor: '#fafbfc',
        zIndex: 999,
        boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.15)'
      }}>
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          bgcolor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" sx={{ 
            mb: 0, 
            fontWeight: 'bold',
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Settings sx={{ fontSize: 20, color: '#4890fe' }} />
            Shape Properties
          </Typography>
          <Tooltip title="Close Properties" arrow>
            <IconButton 
              size="small" 
              onClick={() => {
                fabricCanvasRef.current.discardActiveObject();
                fabricCanvasRef.current.renderAll();
                setSelectedObject(null);
              }}
              sx={{
                color: '#64748b',
                '&:hover': { 
                  bgcolor: 'rgba(100, 116, 139, 0.08)',
                  color: '#374151'
                }
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Box sx={{ p: 2 }}>
          <Stack spacing={3}>
            {commonProperties}
            
            {/* Fill Color - Moved to top as most essential */}
            {selectedObject.type !== 'line' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
                  Fill Color
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {presetColors.map((color) => (
                    <Box
                      key={color}
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: color,
                        borderRadius: 0.5,
                        cursor: 'pointer',
                        border: selectedObject.fill === color ? '2px solid #000' : '1px solid #e0e0e0',
                      }}
                      onClick={() => updateObjectProperty('fill', color)}
                    />
                  ))}
                  <IconButton
                    size="small"
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    sx={{ border: '1px solid #e0e0e0' }}
                  >
                    <Palette fontSize="small" />
                  </IconButton>
                </Box>
                {showColorPicker && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
                    <HexColorPicker
                      color={selectedObject.fill}
                      onChange={(color) => {
                        updateObjectProperty('fill', color);
                        setHexValue(color);
                      }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Hex Color"
                      value={hexValue}
                      onChange={(e) => setHexValue(e.target.value)}
                      onBlur={() => {
                        // Apply color when field loses focus
                        if (/^#([0-9A-F]{3}){1,2}$/i.test(hexValue)) {
                          updateObjectProperty('fill', hexValue);
                        }
                      }}
                      onKeyDown={(e) => {
                        // Apply color on Enter key
                        if (e.key === 'Enter' && /^#([0-9A-F]{3}){1,2}$/i.test(hexValue)) {
                          updateObjectProperty('fill', hexValue);
                          e.preventDefault();
                        }
                      }}
                      sx={{ mt: 2 }}
                      InputProps={{
                        startAdornment: (
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '4px',
                              backgroundColor: hexValue,
                              marginRight: 1,
                              border: '1px solid #e0e0e0'
                            }}
                          />
                        )
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* Size Controls */}
            {selectedObject.type !== 'line' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Size
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Width"
                      type="number"
                      value={Math.round(selectedObject.width || selectedObject.radius * 2)}
                      onChange={(e) => {
                        if (selectedObject.type === 'circle') {
                          updateObjectProperty('radius', parseInt(e.target.value) / 2);
                        } else {
                          updateObjectProperty('width', parseInt(e.target.value));
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Height"
                      type="number"
                      value={Math.round(selectedObject.height || selectedObject.radius * 2)}
                      onChange={(e) => {
                        if (selectedObject.type === 'circle') {
                          updateObjectProperty('radius', parseInt(e.target.value) / 2);
                        } else {
                          updateObjectProperty('height', parseInt(e.target.value));
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Stroke Properties */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Stroke
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Color</InputLabel>
                    <Select
                      value={selectedObject.stroke || 'transparent'}
                      onChange={(e) => updateObjectProperty('stroke', e.target.value)}
                    >
                      <MenuItem value="transparent">None</MenuItem>
                      {presetColors.map((color) => (
                        <MenuItem key={color} value={color}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 16, height: 16, bgcolor: color, borderRadius: 0.5 }} />
                            {color}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Width"
                    type="number"
                    value={selectedObject.strokeWidth || 0}
                    onChange={(e) => updateObjectProperty('strokeWidth', parseInt(e.target.value))}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Border Radius (for rectangles) */}
            {selectedObject.type === 'rect' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Border Radius
                </Typography>
                <Slider
                  value={selectedObject.rx || 0}
                  min={0}
                  max={50}
                  onChange={(e, newValue) => {
                    updateObjectProperty('rx', newValue);
                    updateObjectProperty('ry', newValue);
                  }}
                  valueLabelDisplay="auto"
                />
              </Box>
            )}
          </Stack>
        </Box>
      </Paper>
    );
  }

  // Placeholder image properties
  if (selectedObject.placeholderType === 'image') {
    return (
      <Paper sx={{ 
        position: 'absolute',
        right: 0,
        top: 0,
        width: 320, 
        height: '100%', 
        borderRadius: 0, 
        borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
        overflow: 'auto',
        p: 0,
        bgcolor: '#fafbfc',
        zIndex: 999,
        boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.15)'
      }}>
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          bgcolor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" sx={{ 
            mb: 0, 
            fontWeight: 'bold',
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Settings sx={{ fontSize: 20, color: '#4890fe' }} />
            Placeholder Properties
          </Typography>
          <Tooltip title="Close Properties" arrow>
            <IconButton 
              size="small" 
              onClick={() => {
                fabricCanvasRef.current.discardActiveObject();
                fabricCanvasRef.current.renderAll();
                setSelectedObject(null);
              }}
              sx={{
                color: '#64748b',
                '&:hover': { 
                  bgcolor: 'rgba(100, 116, 139, 0.08)',
                  color: '#374151'
                }
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Box sx={{ p: 2 }}>
          <Stack spacing={3}>
            {commonProperties}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Replace Placeholder
              </Typography>
              <Button
                variant="contained"
                component="label"
                color="primary"
                sx={{ mb: 1 }}
              >
                Upload Image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={e => {
                    const file = e.target.files[0];
                    if (file) {
                      handleImageUpload(file, selectedObject);
                    }
                  }}
                />
              </Button>
              <Typography variant="body2" color="text.secondary">
                This will replace the placeholder with your uploaded image, preserving size and position.
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Paper>
    );
  }

  // Image-specific properties
  if (selectedObject.type === 'image') {
    return (
      <Paper sx={{ 
        position: 'absolute',
        right: 0,
        top: 0,
        width: 320, 
        height: '100%', 
        borderRadius: 0, 
        borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
        overflow: 'auto',
        p: 0,
        bgcolor: '#fafbfc',
        zIndex: 999,
        boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.15)'
      }}>
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          bgcolor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" sx={{ 
            mb: 0, 
            fontWeight: 'bold',
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Settings sx={{ fontSize: 20, color: '#4890fe' }} />
            Image Properties
          </Typography>
          <Tooltip title="Close Properties" arrow>
            <IconButton 
              size="small" 
              onClick={() => {
                fabricCanvasRef.current.discardActiveObject();
                fabricCanvasRef.current.renderAll();
                setSelectedObject(null);
              }}
              sx={{
                color: '#64748b',
                '&:hover': { 
                  bgcolor: 'rgba(100, 116, 139, 0.08)',
                  color: '#374151'
                }
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Box sx={{ p: 2 }}>
          <Stack spacing={3}>
            {commonProperties}
            
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Replace Image
              </Typography>
              <Button
                variant="contained"
                component="label"
                fullWidth
              >
                Upload New Image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        fabric.Image.fromURL(event.target.result, (img) => {
                          img.set({
                            left: selectedObject.left,
                            top: selectedObject.top,
                            scaleX: selectedObject.scaleX,
                            scaleY: selectedObject.scaleY,
                            angle: selectedObject.angle,
                            id: selectedObject.id
                          });
                          fabricCanvasRef.current.remove(selectedObject);
                          fabricCanvasRef.current.add(img);
                          fabricCanvasRef.current.setActiveObject(img);
                          fabricCanvasRef.current.renderAll();
                          setSelectedObject(img);
                          updateHistory();
                        });
                      };
                      reader.readAsDataURL(file);
                    }
                    e.target.value = null;
                  }}
                />
              </Button>
            </Box>

            {/* AI Background Removal Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Background Removal
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      if (selectedObject) {
                        removeBackgroundAI(selectedObject);
                      } else {
                        setSnackbar({ 
                          open: true, 
                          message: 'No image selected for AI background removal', 
                          severity: 'warning' 
                        });
                      }
                    }}
                    disabled={isRemovingBackground}
                    startIcon={isRemovingBackground ? <CircularProgress size={16} /> : <AutoFixHigh />}
                    sx={{ 
                      bgcolor: '#8B5CF6',
                      '&:hover': { 
                        bgcolor: '#7C3AED'
                      },
                      '&:disabled': {
                        bgcolor: '#D1D5DB',
                        color: '#9CA3AF'
                      }
                    }}
                  >
                    Remove Background (AI)
                  </Button>
                </Grid>
              </Grid>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                AI-powered removal works best with people and common objects.
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Paper>
    );
  }

  // Default properties panel
  return (
    <>
    <Paper sx={{ 
      position: 'absolute',
      right: 0,
      top: 0,
      width: 320, 
      height: '100%', 
      borderRadius: 0, 
      borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
      overflow: 'auto',
      p: 0,
      bgcolor: '#fafbfc',
      zIndex: 999,
      boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.15)'
    }}>
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        bgcolor: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h6" sx={{ 
          mb: 0, 
          fontWeight: 'bold',
          color: '#1e293b',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Settings sx={{ fontSize: 20, color: '#4890fe' }} />
          Properties
        </Typography>
        <Tooltip title="Close Properties" arrow>
          <IconButton 
            size="small" 
            onClick={() => {
              fabricCanvasRef.current.discardActiveObject();
              fabricCanvasRef.current.renderAll();
              setSelectedObject(null);
            }}
            sx={{
              color: '#64748b',
              '&:hover': { 
                bgcolor: 'rgba(100, 116, 139, 0.08)',
                color: '#374151'
              }
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Box sx={{ p: 2 }}>
        {commonProperties}
      </Box>
    </Paper>
      
      {/* Font Library Dialog */}
      <Dialog 
        open={showFontLibrary} 
        onClose={() => setShowFontLibrary(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Font Library
          <IconButton onClick={() => setShowFontLibrary(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <FontsPanel 
            onSelectFont={handleFontSelect} 
            fontFamilies={fontFamilies} 
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageRightProperties; 